import * as d3 from 'd3';

export interface ChartSeries {
  id: string;
  name: string;
  color: string;
  data: number[];
  strokeWidth?: number;
  strokeDasharray?: string;
  strokeOpacity?: number;
  showDots?: boolean;
}

export interface ChartMargin {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface ChartHoverValue {
  seriesId: string;
  name: string;
  color: string;
  value: number;
}

export interface ChartHoverInfo {
  phase: number;
  pixelX: number;
  pixelY: number;
  values: ChartHoverValue[];
  isEs?: boolean;
  isEd?: boolean;
}

export interface RenderOcgChartOptions {
  svgElement: SVGSVGElement;
  series: ChartSeries[];
  nPhases: number;
  startPhase?: number;
  esIndices?: number[];
  edIndices?: number[];
  excludedPhaseIndices?: number[];
  currentPhaseIndex?: number;
  showEdEsMarkers?: boolean;
  width?: number;
  height?: number;
  margin?: ChartMargin;
  zoomDomain?: [number, number] | null;
  yMin?: number;
  yMax?: number;
  formatY?: (val: number) => string;
  formatX?: (val: number, startPhase: number) => string;
  onHover?: (hoverInfo: ChartHoverInfo | null) => void;
  onZoomChange?: (newDomain: [number, number] | null) => void;
  onPhaseClick?: (phase: number) => void;
}

/**
 * Renders an OCG or cardiac cycle line chart onto an SVG element using D3.js.
 * Supports multiple series, ED/ES phase markers, excluded phase indicators,
 * current phase lines, drag-to-zoom, hover tracking, and phase-click navigation.
 */
export function renderOcgChart(options: RenderOcgChartOptions): void {
  const svg = d3.select(options.svgElement);
  svg.selectAll('*').remove();

  const nPhases = options.nPhases;
  if (!options.svgElement || nPhases <= 0 || !options.series || options.series.length === 0) {
    return;
  }

  const series = options.series;
  const width = options.width ?? 370;
  const height = options.height ?? 160;
  const margin = options.margin ?? { top: 16, right: 8, bottom: 26, left: 34 };
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  // X Domain (Zoomed or Full)
  const currentXDomain = options.zoomDomain ?? [0, Math.max(1, nPhases - 1)];

  const xScale = d3
    .scaleLinear()
    .domain(currentXDomain)
    .range([margin.left, margin.left + innerWidth]);

  // Y Domain (Auto-fit to visible X range)
  let minY = Infinity;
  let maxY = -Infinity;

  const minXIdx = Math.floor(currentXDomain[0]);
  const maxXIdx = Math.ceil(currentXDomain[1]);

  series.forEach((s) => {
    s.data.forEach((v, i) => {
      if (Number.isFinite(v) && i >= minXIdx && i <= maxXIdx) {
        if (v < minY) minY = v;
        if (v > maxY) maxY = v;
      }
    });
  });

  if (!Number.isFinite(minY) || !Number.isFinite(maxY) || minY === maxY) {
    series.forEach((s) => {
      s.data.forEach((v) => {
        if (Number.isFinite(v)) {
          if (v < minY) minY = v;
          if (v > maxY) maxY = v;
        }
      });
    });
  }

  if (!Number.isFinite(minY) || !Number.isFinite(maxY) || minY === maxY) {
    minY = 0;
    maxY = 100;
  }
  else {
    const yPad = (maxY - minY) * 0.1 || 0.1;
    minY -= yPad;
    maxY += yPad;
  }

  if (options.yMin !== undefined) minY = options.yMin;
  if (options.yMax !== undefined) maxY = options.yMax;

  const yScale = d3
    .scaleLinear()
    .domain([minY, maxY])
    .nice()
    .range([margin.top + innerHeight, margin.top]);

  const defs = svg.append('defs');

  // Clip Path unique per chart instance
  const clipId = `chart-clip-${Math.random().toString(36).substring(2, 9)}`;
  defs
    .append('clipPath')
    .attr('id', clipId)
    .append('rect')
    .attr('x', margin.left)
    .attr('y', margin.top)
    .attr('width', innerWidth)
    .attr('height', innerHeight);

  const g = svg.append('g');

  // Clipped Plot Group for lines, grid, markers, and points
  const gPlot = g.append('g').attr('clip-path', `url(#${clipId})`);

  // Horizontal Grid lines
  const yTicks = yScale.ticks(4);
  gPlot.append('g')
    .attr('class', 'grid-y')
    .selectAll('line')
    .data(yTicks)
    .enter()
    .append('line')
    .attr('x1', margin.left)
    .attr('x2', margin.left + innerWidth)
    .attr('y1', d => yScale(d))
    .attr('y2', d => yScale(d))
    .attr('stroke', 'rgba(255, 255, 255, 0.08)')
    .attr('stroke-dasharray', '2,2');

  // X Axis (adjusted for startPhase)
  const startPh = options.startPhase ?? 0;
  const xSpan = currentXDomain[1] - currentXDomain[0];
  const fmtX = options.formatX ?? ((d: number, sp: number) => `${Math.round(Number(d)) + sp + 1}`);

  const xAxis = d3
    .axisBottom(xScale)
    .ticks(Math.min(8, Math.max(2, Math.round(xSpan))))
    .tickFormat(d => fmtX(Number(d), startPh));

  g.append('g')
    .attr('transform', `translate(0, ${margin.top + innerHeight})`)
    .call(xAxis)
    .attr('color', 'rgba(255, 255, 255, 0.4)')
    .selectAll('text')
    .attr('font-size', '9px')
    .attr('fill', '#94a3b8');

  // Y Axis
  const fmtY = options.formatY ?? ((d: number) => Number(d).toLocaleString(undefined, { maximumFractionDigits: 1 }));
  const yAxis = d3
    .axisLeft(yScale)
    .ticks(4)
    .tickFormat(d => fmtY(Number(d)));

  g.append('g')
    .attr('transform', `translate(${margin.left}, 0)`)
    .call(yAxis)
    .attr('color', 'rgba(255, 255, 255, 0.4)')
    .selectAll('text')
    .attr('font-size', '9px')
    .attr('fill', '#94a3b8');

  // Line Generator for valid data
  const lineGenerator = d3
    .line<number>()
    .defined(d => Number.isFinite(d))
    .x((_, i) => xScale(i))
    .y(d => yScale(d))
    .curve(d3.curveMonotoneX);

  // Draw Curve Lines inside gPlot
  series.forEach((s) => {
    const path = gPlot.append('path')
      .datum(s.data)
      .attr('fill', 'none')
      .attr('stroke', s.color)
      .attr('stroke-width', s.strokeWidth ?? 1.5)
      .attr('stroke-linecap', 'round')
      .attr('stroke-linejoin', 'round')
      .attr('d', lineGenerator);

    if (s.strokeDasharray) {
      path.attr('stroke-dasharray', s.strokeDasharray);
    }
    if (s.strokeOpacity !== undefined) {
      path.attr('stroke-opacity', s.strokeOpacity);
    }

    // Dots
    if (s.showDots !== false) {
      s.data.forEach((val, i) => {
        if (Number.isFinite(val)) {
          gPlot.append('circle')
            .attr('class', `dot-${s.id}`)
            .attr('cx', xScale(i))
            .attr('cy', yScale(val))
            .attr('r', 1.5)
            .attr('fill', s.color)
            .attr('stroke', '#0f172a')
            .attr('stroke-width', 0.5);
        }
      });
    }
  });

  // Excluded Phases Overlay
  if (options.excludedPhaseIndices && options.excludedPhaseIndices.length > 0) {
    options.excludedPhaseIndices.forEach((exIdx) => {
      if (exIdx >= 0 && exIdx < nPhases) {
        const exX = xScale(exIdx);
        gPlot.append('line')
          .attr('x1', exX)
          .attr('x2', exX)
          .attr('y1', margin.top)
          .attr('y2', margin.top + innerHeight)
          .attr('stroke', '#ef4444')
          .attr('stroke-width', 1.2)
          .attr('stroke-dasharray', '4,4')
          .attr('opacity', 0.5);
      }
    });
  }

  // End Diastole (ED) Phase Markers
  if (options.showEdEsMarkers !== false && options.edIndices && options.edIndices.length > 0) {
    options.edIndices.forEach((edIdx) => {
      if (edIdx >= 0 && edIdx < nPhases) {
        const edX = xScale(edIdx);
        gPlot.append('line')
          .attr('x1', edX)
          .attr('x2', edX)
          .attr('y1', margin.top)
          .attr('y2', margin.top + innerHeight)
          .attr('stroke', '#ff5161')
          .attr('stroke-width', 1)
          .attr('stroke-dasharray', '3,3')
          .attr('opacity', 0.85);

        g.append('text')
          .attr('x', edX)
          .attr('y', margin.top - 4)
          .attr('text-anchor', 'middle')
          .attr('fill', '#fefefe')
          .attr('font-size', '9px')
          .attr('font-weight', '700')
          .text('ED');
      }
    });
  }

  // End Systole (ES) Phase Markers
  if (options.showEdEsMarkers !== false && options.esIndices && options.esIndices.length > 0) {
    options.esIndices.forEach((esIdx) => {
      if (esIdx >= 0 && esIdx < nPhases) {
        const esX = xScale(esIdx);
        gPlot.append('line')
          .attr('x1', esX)
          .attr('x2', esX)
          .attr('y1', margin.top)
          .attr('y2', margin.top + innerHeight)
          .attr('stroke', '#00fe9b')
          .attr('stroke-width', 1)
          .attr('stroke-dasharray', '3,3')
          .attr('opacity', 0.9);

        g.append('text')
          .attr('x', esX)
          .attr('y', margin.top - 4)
          .attr('text-anchor', 'middle')
          .attr('fill', '#fefefe')
          .attr('font-size', '9px')
          .attr('font-weight', '700')
          .text('ES');
      }
    });
  }

  // Active Viewer Frame / Phase Indicator (vertical line)
  if (options.currentPhaseIndex !== undefined) {
    const relativePhaseIdx = options.currentPhaseIndex - startPh;
    if (relativePhaseIdx >= 0 && relativePhaseIdx < nPhases) {
      const currX = xScale(relativePhaseIdx);
      gPlot.append('line')
        .attr('x1', currX)
        .attr('x2', currX)
        .attr('y1', margin.top)
        .attr('y2', margin.top + innerHeight)
        .attr('stroke', '#ffffff')
        .attr('stroke-width', 1.8)
        .attr('stroke-dasharray', '3,3')
        .attr('opacity', 0.95);
    }
  }

  // Hover Interaction Group
  const hoverGroup = gPlot.append('g').style('display', 'none');
  const hoverLine = hoverGroup
    .append('line')
    .attr('y1', margin.top)
    .attr('y2', margin.top + innerHeight)
    .attr('stroke', 'rgba(255, 255, 255, 0.5)')
    .attr('stroke-width', 1.2)
    .attr('stroke-dasharray', '2,2');

  const hoverDots = series.map((s) => {
    return hoverGroup
      .append('circle')
      .attr('r', 4)
      .attr('fill', s.color)
      .attr('stroke', '#ffffff')
      .attr('stroke-width', 1.5);
  });

  // Translucent Drag-to-Zoom Selection Box
  const selectionRect = g
    .append('rect')
    .attr('class', 'zoom-selection-rect')
    .attr('y', margin.top)
    .attr('height', innerHeight)
    .attr('fill', 'rgba(59, 130, 246, 0.25)')
    .attr('stroke', 'rgba(59, 130, 246, 0.8)')
    .attr('stroke-width', 1.2)
    .attr('stroke-dasharray', '3,3')
    .style('display', 'none');

  let dragStartPixelX: number | null = null;
  let isMouseDragging = false;

  // Chart Overlay Pointer Handlers for Drag Selection & Hover
  svg
    .append('rect')
    .attr('x', margin.left)
    .attr('y', margin.top)
    .attr('width', innerWidth)
    .attr('height', innerHeight)
    .attr('fill', 'transparent')
    .attr('cursor', options.onPhaseClick ? 'crosshair' : 'default')
    .on('mousedown', (event: MouseEvent) => {
      if (event.button !== 0) return;
      const [mouseX] = d3.pointer(event);
      dragStartPixelX = mouseX;
      isMouseDragging = true;
      hoverGroup.style('display', 'none');
      options.onHover?.(null);
    })
    .on('mousemove', (event: MouseEvent) => {
      const [mouseX] = d3.pointer(event);
      const clampedX = Math.max(margin.left, Math.min(margin.left + innerWidth, mouseX));

      if (isMouseDragging && dragStartPixelX !== null) {
        const x0 = Math.min(dragStartPixelX, clampedX);
        const w = Math.abs(clampedX - dragStartPixelX);
        if (w > 3) {
          selectionRect
            .style('display', null)
            .attr('x', x0)
            .attr('width', w);
        }
        hoverGroup.style('display', 'none');
        options.onHover?.(null);
      }
      else {
        const phaseVal = xScale.invert(clampedX);
        const nearestPhase = Math.max(0, Math.min(nPhases - 1, Math.round(phaseVal)));

        const snapX = xScale(nearestPhase);
        hoverGroup.style('display', null);
        hoverLine.attr('x1', snapX).attr('x2', snapX);

        const hoverValues: ChartHoverValue[] = [];
        series.forEach((s, idx) => {
          const val = s.data[nearestPhase];
          if (Number.isFinite(val) && hoverDots[idx]) {
            hoverDots[idx]
              .style('display', null)
              .attr('cx', snapX)
              .attr('cy', yScale(val));
            hoverValues.push({
              seriesId: s.id,
              name: s.name,
              color: s.color,
              value: val,
            });
          }
          else if (hoverDots[idx]) {
            hoverDots[idx].style('display', 'none');
          }
        });

        const isEs = options.esIndices?.includes(nearestPhase) ?? false;
        const isEd = options.edIndices?.includes(nearestPhase) ?? false;

        options.onHover?.({
          phase: nearestPhase,
          pixelX: snapX,
          pixelY: margin.top,
          values: hoverValues,
          isEs,
          isEd,
        });
      }
    })
    .on('mouseup', (event: MouseEvent) => {
      if (!isMouseDragging || dragStartPixelX === null) return;
      const [mouseX] = d3.pointer(event);
      const clampedX = Math.max(margin.left, Math.min(margin.left + innerWidth, mouseX));
      const dragDist = Math.abs(clampedX - dragStartPixelX);

      isMouseDragging = false;
      selectionRect.style('display', 'none');

      if (dragDist > 6) {
        const px0 = Math.min(dragStartPixelX, clampedX);
        const px1 = Math.max(dragStartPixelX, clampedX);
        let ph0 = xScale.invert(px0);
        let ph1 = xScale.invert(px1);

        ph0 = Math.max(0, Math.min(nPhases - 1, ph0));
        ph1 = Math.max(0, Math.min(nPhases - 1, ph1));

        if (ph1 - ph0 > 0.3) {
          options.onZoomChange?.([ph0, ph1]);
        }
      }
      else {
        // Single left click: trigger phase click
        const phaseVal = xScale.invert(clampedX);
        const nearestRelativePhase = Math.max(0, Math.min(nPhases - 1, Math.round(phaseVal)));
        const targetImageIdx = nearestRelativePhase + startPh;
        options.onPhaseClick?.(targetImageIdx);
      }
      dragStartPixelX = null;
    })
    .on('mouseleave', () => {
      if (isMouseDragging) {
        isMouseDragging = false;
        selectionRect.style('display', 'none');
        dragStartPixelX = null;
      }
      hoverGroup.style('display', 'none');
      options.onHover?.(null);
    })
    .on('dblclick', () => {
      options.onZoomChange?.(null);
    });
}
